import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { cx } from '@linaria/atomic';
import { Button } from '../button/button.component';
import { PopoverDialog } from '../popover/dialog/popover-dialog.component';
import { usePopoverDialog } from '../popover/dialog/use-popover-dialog';
import {
  backwardSkipButtonStyle,
  compactControlsStyle,
  compactCollapsedStyle,
  compactMenuProgressStyle,
  compactMenuControlStyle,
  compactMenuStyle,
  compactRootStyle,
  compactTimeStyle,
  hiddenAudioStyle,
  inlineProgressGroupStyle,
  inlineProgressStyle,
  inlineControlsStyle,
  inlineRootStyle,
  inlineTimeStyle,
  playbackRootStyle,
  progressStyle,
  speedButtonStyle,
  speedControlStyle,
  timeRowStyle,
} from './audio-playback.styles';

type AudioPlaybackVariant = 'full' | 'compact' | 'responsive';

type AudioPlaybackState = {
  currentTime: number;
  duration: number;
  hasError: boolean;
  isLoaded: boolean;
  isPlaying: boolean;
  playbackRate: number;
};

type AudioPlaybackAction =
  | { type: 'audioEnd' }
  | { type: 'currentTime'; payload: number }
  | { type: 'duration'; payload: number }
  | { type: 'hasError'; payload: boolean }
  | { type: 'isLoaded'; payload: boolean }
  | { type: 'isPlaying'; payload: boolean }
  | { type: 'playbackRate'; payload: number }
  | { type: 'reset'; payload: number };

export type AudioPlaybackProps = {
  autoplay?: boolean;
  className?: string;
  loop?: boolean;
  muted?: boolean;
  onEnded?: () => void;
  onPlay?: () => void;
  onPlaybackRateChange?: (playbackRate: number) => void;
  playbackRate?: number;
  playbackRateOptions?: number[];
  preload?: 'auto' | 'metadata' | 'none';
  responsiveBreakpoint?: number;
  showPlaybackRate?: boolean;
  showSkipControls?: boolean;
  showCurrentTime?: boolean;
  singlePlayer?: boolean;
  skipSeconds?: number;
  src: string;
  style?: React.CSSProperties;
  tabIndex?: number;
  minTrackLength?: number;
  trackingId?: string;
  variant?: AudioPlaybackVariant;
};

const initialState: AudioPlaybackState = {
  currentTime: 0,
  duration: 0,
  hasError: false,
  isLoaded: false,
  isPlaying: false,
  playbackRate: 1,
};

const reducer = (state: AudioPlaybackState, action: AudioPlaybackAction): AudioPlaybackState => {
  switch (action.type) {
    case 'audioEnd':
      return { ...state, currentTime: 0, isPlaying: false };
    case 'currentTime':
      return { ...state, currentTime: action.payload };
    case 'duration':
      return { ...state, duration: action.payload };
    case 'hasError':
      return { ...state, hasError: action.payload };
    case 'isLoaded':
      return { ...state, isLoaded: action.payload };
    case 'isPlaying':
      return { ...state, isPlaying: action.payload };
    case 'playbackRate':
      return { ...state, playbackRate: action.payload };
    case 'reset':
      return { ...initialState, playbackRate: action.payload };
    default:
      return state;
  }
};

const formatTime = (time: number) => {
  if (!Number.isFinite(time)) return '0:00';
  return `${Math.floor(time / 60)}:${String(Math.floor(time % 60)).padStart(2, '0')}`;
};

const formatPlaybackRate = (rate: number) => rate.toFixed(1);

export const AudioPlayback = ({
  autoplay = false,
  className,
  loop = false,
  muted = false,
  onEnded,
  onPlay,
  onPlaybackRateChange,
  playbackRate: playbackRateProp = 1,
  playbackRateOptions = [0.7, 1, 1.5, 1.7, 2],
  preload = 'metadata',
  responsiveBreakpoint = 320,
  showCurrentTime = false,
  showPlaybackRate = false,
  showSkipControls = false,
  singlePlayer = false,
  skipSeconds = 15,
  src,
  style,
  tabIndex = 0,
  minTrackLength = 138,
  trackingId = 'audioPlayback',
  variant = 'full',
}: AudioPlaybackProps) => {
  const audioElement = useRef<HTMLAudioElement>(null);
  const responsiveContainer = useRef<HTMLDivElement>(null);
  // Tracks the layout selected from the responsive container width.
  const [shouldUseCompactLayout, setShouldUseCompactLayout] = useState(false);
  const [{ currentTime, duration, hasError, isLoaded, isPlaying, playbackRate }, dispatch] = useReducer(
    reducer,
    playbackRateProp,
    (initialPlaybackRate) => ({ ...initialState, playbackRate: initialPlaybackRate })
  );
  const audioPlaybackRoot = useRef<HTMLDivElement>(null);
  const hasLoaded = useRef(false);
  const isResponsive = variant === 'responsive';
  const isCompact = variant === 'compact' || (isResponsive && shouldUseCompactLayout);
  const {
    close: closeCompactMenu,
    getDialogProps,
    getTriggerProps,
    isOpen: isCompactMenuOpen,
  } = usePopoverDialog<HTMLButtonElement>({
    placement: 'right',
    middlewareOptions: {
      flip: {
        fallbackPlacements: ['bottom', 'top'],
      },
      offset: { mainAxis: 8 },
    },
    interactionOptions: {
      dismiss: {
        outsidePress: (event) => !(audioPlaybackRoot.current?.contains(event.target as Node) ?? false),
      },
    },
  });
  const shouldShowTime = isCompact || showCurrentTime;
  const shouldShowSkip = isCompact || showSkipControls;
  const shouldShowRate = isCompact || showPlaybackRate;

  useLayoutEffect(() => {
    if (!isResponsive) return;

    const container = responsiveContainer.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const updateVariant = (width: number) => setShouldUseCompactLayout(width < responsiveBreakpoint);
    updateVariant(container.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) updateVariant(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isResponsive, responsiveBreakpoint]);

  useEffect(() => {
    if (isResponsive && !isCompact) closeCompactMenu();
  }, [closeCompactMenu, isCompact, isResponsive]);

  useEffect(() => {
    if (!isCompactMenuOpen || !isCompact) return;

    const target = audioPlaybackRoot.current;
    if (!target || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) closeCompactMenu();
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [closeCompactMenu, isCompact, isCompactMenuOpen]);

  useEffect(() => {
    dispatch({ type: 'reset', payload: playbackRateProp });
    hasLoaded.current = false;
  }, [src]);

  useEffect(() => {
    dispatch({ type: 'playbackRate', payload: playbackRateProp });
  }, [playbackRateProp]);

  useEffect(() => {
    if (audioElement.current) audioElement.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const setPlaybackRate = (rate: number) => {
    if (audioElement.current) audioElement.current.playbackRate = rate;
    dispatch({ type: 'playbackRate', payload: rate });
    onPlaybackRateChange?.(rate);
  };

  const cyclePlaybackRate = () => {
    if (playbackRateOptions.length === 0) return;
    const currentIndex = playbackRateOptions.indexOf(playbackRate);
    const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % playbackRateOptions.length;
    const nextRate = playbackRateOptions.find((_, index) => index === nextIndex);
    if (nextRate === undefined) return;
    setPlaybackRate(nextRate);
  };

  const setCurrentTime = (nextTime: number) => {
    if (!audioElement.current || duration <= 0) return;
    const boundedTime = Math.min(Math.max(nextTime, 0), duration);
    audioElement.current.currentTime = boundedTime;
    dispatch({ type: 'currentTime', payload: boundedTime });
  };

  const playbackButtonTrackingId = `${trackingId}-btn-${isPlaying ? 'pause' : 'play'}`;

  const togglePlayback = () => {
    if (!audioElement.current || !isLoaded || hasError) return;
    if (isPlaying) {
      audioElement.current.pause();
      dispatch({ type: 'isPlaying', payload: false });
    } else {
      void audioElement.current.play().catch(() => dispatch({ type: 'hasError', payload: true }));
      dispatch({ type: 'isPlaying', payload: true });
    }
  };

  const renderPlaybackButton = (buttonTrackingId: string, size: 'small' | 'tiny' = 'small') => (
    <Button
      disabled={hasError}
      iconName={isPlaying ? 'pause' : 'play'}
      label={isPlaying ? 'Pause' : 'Play'}
      loading={!isLoaded && !hasError}
      onClick={togglePlayback}
      size={size}
      trackingId={buttonTrackingId}
      variant='secondary'
    />
  );

  const renderScrubber = (scrubberClassName: string, timeClassName: string, showTime = true) => (
    <>
      <input
        aria-label='Audio progress'
        className={scrubberClassName}
        disabled={!isLoaded || hasError}
        max={duration}
        min='0'
        onChange={(event) => setCurrentTime(Number(event.target.value))}
        step='0.01'
        style={
          {
            ['--audio-progress' as string]: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
          } as React.CSSProperties
        }
        tabIndex={tabIndex}
        type='range'
        value={currentTime}
      />
      {showTime && (
        <span className={timeClassName}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      )}
    </>
  );

  const pauseOtherPlayers = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    if (!singlePlayer) return;
    document.querySelectorAll('[data-audio-playback] audio').forEach((element) => {
      if (element !== event.currentTarget) (element as HTMLAudioElement).pause();
    });
  };

  const rootClassName = cx(playbackRootStyle, isCompact ? compactRootStyle : inlineRootStyle, className);

  return (
    <div ref={responsiveContainer} style={isResponsive ? { minWidth: 0, width: '100%' } : undefined}>
      <div
        className={rootClassName}
        data-audio-playback
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
        ref={audioPlaybackRoot}
        style={isResponsive && !isCompact ? { ...style, minWidth: `${responsiveBreakpoint}px` } : style}
      >
        {isCompact ? (
          <>
            <div className={compactCollapsedStyle}>
              {renderPlaybackButton(playbackButtonTrackingId, 'tiny')}
              <span className={compactTimeStyle}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              <div className={compactMenuControlStyle}>
                <Button
                  {...getTriggerProps()}
                  aria-expanded={isCompactMenuOpen}
                  aria-haspopup='dialog'
                  iconName='more'
                  label={isCompactMenuOpen ? 'Hide audio controls' : 'Show audio controls'}
                  size='tiny'
                  trackingId={`${trackingId}-btn-menu`}
                  variant='secondary'
                />
              </div>
            </div>
            <PopoverDialog
              aria-label='Audio controls'
              className={compactMenuStyle}
              {...getDialogProps()}
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              returnFocus
            >
              <div className={compactControlsStyle}>
                <Button
                  className={backwardSkipButtonStyle}
                  disabled={!isLoaded || hasError || duration <= 0}
                  iconName='refresh'
                  label={`Back ${skipSeconds} seconds`}
                  onClick={() => setCurrentTime(currentTime - skipSeconds)}
                  size='tiny'
                  trackingId={`${trackingId}-btn-back`}
                  variant='secondary'
                />
                <Button
                  disabled={!isLoaded || hasError || duration <= 0}
                  iconName='refresh'
                  label={`Forward ${skipSeconds} seconds`}
                  onClick={() => setCurrentTime(currentTime + skipSeconds)}
                  size='tiny'
                  trackingId={`${trackingId}-btn-forward`}
                  variant='secondary'
                />
              </div>
              <div className={compactMenuProgressStyle}>{renderScrubber(progressStyle, timeRowStyle)}</div>
              <div className={speedControlStyle}>
                <Button
                  aria-label={`Playback speed ${formatPlaybackRate(playbackRate)}x`}
                  className={speedButtonStyle}
                  onClick={cyclePlaybackRate}
                  size='tiny'
                  trackingId={`${trackingId}-btn-speed`}
                  variant='secondary'
                >
                  {formatPlaybackRate(playbackRate)}x
                </Button>
              </div>
            </PopoverDialog>
          </>
        ) : (
          <>
            <div className={inlineControlsStyle}>
              {shouldShowSkip && (
                <>
                  <Button
                    className={backwardSkipButtonStyle}
                    disabled={!isLoaded || hasError || duration <= 0}
                    iconName='refresh'
                    label={`Back ${skipSeconds} seconds`}
                    onClick={() => setCurrentTime(currentTime - skipSeconds)}
                    size='small'
                    trackingId={`${trackingId}-btn-back`}
                    variant='secondary'
                  />
                  {renderPlaybackButton(playbackButtonTrackingId)}
                  <Button
                    disabled={!isLoaded || hasError || duration <= 0}
                    iconName='refresh'
                    label={`Forward ${skipSeconds} seconds`}
                    onClick={() => setCurrentTime(currentTime + skipSeconds)}
                    size='small'
                    trackingId={`${trackingId}-btn-forward`}
                    variant='secondary'
                  />
                </>
              )}
              {!shouldShowSkip && renderPlaybackButton(playbackButtonTrackingId)}
            </div>

            <div className={inlineProgressGroupStyle} style={{ minWidth: `${minTrackLength}px` }}>
              {renderScrubber(cx(progressStyle, inlineProgressStyle), inlineTimeStyle, shouldShowTime)}
            </div>
            {shouldShowRate && (
              <div className={speedControlStyle}>
                <Button
                  aria-label={`Playback speed ${formatPlaybackRate(playbackRate)}x`}
                  className={speedButtonStyle}
                  onClick={cyclePlaybackRate}
                  size='tiny'
                  trackingId={`${trackingId}-btn-speed`}
                  variant='secondary'
                >
                  {formatPlaybackRate(playbackRate)}x
                </Button>
              </div>
            )}
          </>
        )}
        <audio
          autoPlay={autoplay}
          className={hiddenAudioStyle}
          loop={loop}
          muted={muted}
          onCanPlay={() => {
            if (!hasLoaded.current) {
              hasLoaded.current = true;
              dispatch({ type: 'isLoaded', payload: true });
            }
          }}
          onDurationChange={(event) =>
            dispatch({
              type: 'duration',
              payload: Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0,
            })
          }
          onEnded={() => {
            dispatch({ type: 'audioEnd' });
            onEnded?.();
          }}
          onError={() => dispatch({ type: 'hasError', payload: true })}
          onLoadedMetadata={() => dispatch({ type: 'isLoaded', payload: true })}
          onPause={() => dispatch({ type: 'isPlaying', payload: false })}
          onPlay={(event) => {
            pauseOtherPlayers(event);
            dispatch({ type: 'isPlaying', payload: true });
            onPlay?.();
          }}
          onTimeUpdate={(event) => dispatch({ type: 'currentTime', payload: event.currentTarget.currentTime })}
          preload={preload}
          ref={audioElement}
          src={src}
        />
      </div>
    </div>
  );
};
